import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, Tag, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined colors for event categories
const COLOR_PALETTE = [
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981', 
  '#06b6d4', '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#f43f5e', '#64748b'
];

export default function EventCategoriesManager() {
  const { token } = useAuth();
  
  const [categories, setCategories] = useState({ system_categories: [], custom_categories: [] });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    name: '',
    color: '#6366f1',
    description: ''
  });

  const fetchCategories = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/event-categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCategories(await res.json());
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const resetForm = () => {
    setForm({ name: '', color: '#6366f1', description: '' });
    setEditingCategory(null);
  };

  const handleCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setForm({
      name: category.name,
      color: category.color || '#6366f1',
      description: category.description || ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', form.name);
      formData.append('color', form.color);
      formData.append('description', form.description);

      const url = editingCategory 
        ? `${API_URL}/api/case-management/event-categories/${editingCategory.id}`
        : `${API_URL}/api/case-management/event-categories`;
      
      const res = await fetch(url, {
        method: editingCategory ? 'PUT' : 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        toast.success(editingCategory ? 'Category updated' : 'Category created');
        setShowDialog(false);
        fetchCategories();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save category');
      }
    } catch (error) {
      toast.error('Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/case-management/event-categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Category deleted');
        fetchCategories();
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <Card data-testid="event-categories-manager">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Event Categories
            </CardTitle>
            <CardDescription>
              Create custom event categories for your firm's case management
            </CardDescription>
          </div>
          <Button onClick={handleCreate} data-testid="create-category-btn">
            <Plus className="w-4 h-4 mr-1" />
            Add Category
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* System Categories */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-slate-500 mb-3">System Categories (Default)</h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {categories.system_categories.map(cat => (
              <div 
                key={cat.id} 
                className="flex items-center gap-2 p-2 border rounded-lg bg-slate-50"
              >
                <span 
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: cat.color }}
                />
                <span className="text-sm">{cat.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Categories */}
        <div>
          <h4 className="text-sm font-medium text-slate-500 mb-3">Custom Categories</h4>
          {categories.custom_categories.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-4">
              No custom categories yet. Create one to organize your events.
            </p>
          ) : (
            <div className="space-y-2">
              {categories.custom_categories.map(cat => (
                <div 
                  key={cat.id} 
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors"
                  data-testid={`category-${cat.id}`}
                >
                  <div className="flex items-center gap-3">
                    <span 
                      className="w-4 h-4 rounded-full flex-shrink-0"
                      style={{ backgroundColor: cat.color }}
                    />
                    <div>
                      <span className="font-medium">{cat.name}</span>
                      {cat.description && (
                        <p className="text-xs text-slate-500">{cat.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(cat)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(cat.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'New Category'}</DialogTitle>
            <DialogDescription>
              {editingCategory ? 'Update category details' : 'Create a custom event category'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Settlement Conference"
                data-testid="category-name-input"
              />
            </div>

            <div>
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {COLOR_PALETTE.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === color ? 'border-slate-900 scale-110' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setForm(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of when to use this category..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-category-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Saving...
                </>
              ) : (
                editingCategory ? 'Update' : 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
